import { DOCUMENT, isPlatformServer } from '@angular/common';

import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { Component, Inject, Input, PLATFORM_ID, Renderer2 } from '@angular/core';
import { ImagePreloadDirective } from '@core-mkt/services/image-preload/image-preload.directive';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import { PricingService } from '@core-mkt/services/pricing/pricing.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import { CategoryWidgetComponent } from '@core-mkt/shared/components/category-widget/category-widget/category-widget.component';
import { VisionModalService } from '@core-mkt/shared/components/vision-modal/vision-modal.service';
import type { BottomRightContent, CategoryWidgets } from '../top-section';
import { TopSection } from '../top-section';

@Component({
  selector: 'top-section-wysiwyg',
  templateUrl: './top-section-wysiwyg.component.html',
  styleUrls: ['./top-section-wysiwyg.component.scss'],
  standalone: true,
  imports: [CategoryWidgetComponent, ImagePreloadDirective],
})
export class TopSectionWysiwygComponent implements OnInit, OnChanges {
  @Input() rightContent: string;
  @Input() bottomRightContent: BottomRightContent[];

  // Local properties for template consumption
  processedRightContent: string;
  processedBottomRightContent: BottomRightContent[] = [];

  private originalRightContent: string;
  private originalBottomRightContent: BottomRightContent[];
  @Input() categoryWidgets: CategoryWidgets[];
  @Input() data: TopSection;
  @Input() product: XGritCompleteProduct;

  constructor(
    private redactor: WysiwygRedactorService,
    private imgixService: ImgixService,
    private pricing: PricingService,
    private modal: VisionModalService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: object,
  ) {}

  ngOnInit() {
    // Store original content before processing
    this.storeOriginalContent();

    // Process content for initial render
    this.processInitialContent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When product becomes available, update content with actual pricing
    if (changes.product && this.product) {
      this.updateContentWithPricing();
    }
  }

  storeOriginalContent(): void {
    this.originalRightContent = this.rightContent;
    this.originalBottomRightContent = this.bottomRightContent
      ? JSON.parse(JSON.stringify(this.bottomRightContent))
      : [];
  }

  processInitialContent(): void {
    // Process rightContent with placeholder removal for initial render
    const firstImageSrc = this.rightContent ? this.redactor.extractFirstImageSrc(this.rightContent) : null;
    this.processedRightContent = this.rightContent
      ? this.redactor.processContentWithPlaceholderRemoval(this.rightContent, 'lcp')
      : this.rightContent;
    if (firstImageSrc && isPlatformServer(this.platformId)) {
      const link = this.document.createElement('link');
      link.setAttribute('rel', 'preload');
      link.setAttribute('as', 'image');
      link.setAttribute('fetchpriority', 'high');
      link.setAttribute('href', firstImageSrc);
      this.renderer.appendChild(this.document.head, link);
    }

    // Process bottomRightContent items and create local copies
    if (this.bottomRightContent?.length > 0) {
      this.processedBottomRightContent = this.bottomRightContent.map((item) => ({
        ...item,
        sealIconText: this.redactor.processContentWithPlaceholderRemoval(item.sealIconText),
      }));
    }
  }

  updateContentWithPricing(): void {
    // Update rightContent if it had pricing placeholders
    if (this.redactor.containsPricingPlaceholders(this.originalRightContent)) {
      this.processedRightContent = this.pricing.getPricingText(this.product, this.originalRightContent);
    }

    // Update bottomRightContent array items if they had pricing placeholders
    if (this.processedBottomRightContent?.length > 0 && this.originalBottomRightContent) {
      this.processedBottomRightContent.forEach((item, index) => {
        const originalItem = this.originalBottomRightContent[index];
        if (originalItem && this.redactor.containsPricingPlaceholders(originalItem.sealIconText)) {
          item.sealIconText = this.pricing.getPricingText(this.product, originalItem.sealIconText);
        }
      });
    }
  }

  activateModalWrapper(event: MouseEvent): void {
    if (
      (event.target as HTMLElement).classList.contains('highlighted-text-modal-1') &&
      this.data.highlightedTextModal1.length > 0
    ) {
      const modalContent = this.data.highlightedTextModal1[0].modalContent;
      this.modal.openModal({ showModal: true, modalContent });
    }
    if (
      (event.target as HTMLElement).classList.contains('highlighted-text-modal-2') &&
      this.data.highlightedTextModal2.length > 0
    ) {
      const modalContent = this.data.highlightedTextModal2[0].modalContent;
      this.modal.openModal({ showModal: true, modalContent });
    }
  }
}
